import { useMutation } from '@apollo/client/react';
import type { FC } from 'react';

import { ProductAttributesTrackingAction, ProductAttributesTrackingCategory } from '@zen/Products/types';
import { useNotification } from '@zen/utils/hooks/useNotification';
import useTracking from '@zen/utils/hooks/useTracking';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';
import { performFormMutation } from '@zen/utils/performMutation';

import ProductAttributeForm from '../components/ProductAttributeForm';
import {
  UpdateProductAttributeDocument,
  type UpdateProductAttributeMutationResult,
  type UpdateProductAttributeMutationVariables
} from '../graphql';
import type { CoreProductAttribute } from '../types';

interface Props {
  item: CoreProductAttribute;
  onClose: () => void;
}

const EditProductAttribute: FC<Props> = ({ item, onClose }) => {
  const { addSuccess, addError } = useNotification();
  const { trackEvent } = useTracking();
  const [updateAttribute] = useMutation<UpdateProductAttributeMutationResult, UpdateProductAttributeMutationVariables>(
    UpdateProductAttributeDocument
  );

  const trackAttributeUpdate = (attributeName: string): void => {
    trackEvent({
      category: ProductAttributesTrackingCategory,
      action: ProductAttributesTrackingAction.ATTRIBUTE_UPDATED,
      label: attributeName,
      properties: {
        attributeName
      }
    });
  };

  const handleSubmit = async (values: Pick<CoreProductAttribute, 'name' | 'type'>): Promise<IOkOrErrorResult> => {
    trackAttributeUpdate(values.name);

    return performFormMutation({
      mutationFn: () =>
        updateAttribute({
          refetchQueries: ['productAttributes'],
          variables: {
            input: {
              id: item.id,
              name: values.name
            }
          }
        }),
      onError: () => addError()
    });
  };

  const handleSuccess = (): void => {
    addSuccess('Product attribute updated successfully.');
    onClose();
  };

  return (
    <ProductAttributeForm
      buttonText="Save changes"
      initialValues={item}
      onCancel={onClose}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
    />
  );
};

export default EditProductAttribute;
